import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Avatar,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  Divider,
  Stack,
  TextField,
  useMediaQuery,
  Snackbar,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
} from "@mui/material";
import { Edit, ArrowBack, PictureAsPdf } from "@mui/icons-material";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api from "../../api/api";
import teacherAvatar from "./teacheravarta.png";
import html2pdf from "html2pdf.js";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

const ATTENDANCE_OPTIONS = ["absent", "present", "honor_class", "late"];

const statusColor = (status) => {
  switch (status) {
    case "present": return "success";
    case "absent": return "error";
    case "late": return "warning";
    case "honor_class": return "info";
    default: return "default";
  }
};

const TeacherAttendanceData = () => {
  const [teachers, setTeachers] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  const [attendanceStatus, setAttendanceStatus] = useState("");
  const [month, setMonth] = useState(new Date().toLocaleString("default", { month: "long" }));
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);

  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [attendanceCounts, setAttendanceCounts] = useState({});
  const [attendanceRemark, setAttendanceRemark] = useState("");

  const [showDetails, setShowDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const isMobile = useMediaQuery("(max-width:900px)");
  const detailsRef = useRef(null);

  useEffect(() => { fetchTeachers(); }, []);
  useEffect(() => { filterTeachers(searchTerm); }, [teachers, searchTerm]);

  useEffect(() => {
    if (selectedTeacher && showDetails) fetchTeacherAttendanceLogs(selectedTeacher.id, month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const fetchTeachers = async () => {
    try {
      const res = await api.get("teachers-listing/");
      setTeachers(res.data);
      setFilteredTeachers(res.data);
    } catch (err) {
      console.error(err);
      showToast("Error fetching teachers", "error");
    }
  };

  const filterTeachers = (term) => {
    if (!term) setFilteredTeachers(teachers);
    else {
      const filtered = teachers.filter((teacher) =>
        teacher.full_name.toLowerCase().includes(term.toLowerCase())
      );
      setFilteredTeachers(filtered);
    }
  };

  const handleTakeAttendance = (teacher) => {
    setSelectedTeacher(teacher);
    setShowDetails(false);
    setAttendanceStatus("");
    setAttendanceLogs([]);
    setAttendanceCounts({});
    setAttendanceRemark("");
  };

  const handleSaveAttendance = async () => {
    if (!attendanceStatus) return showToast("Please select attendance status", "error");
    try {
      await api.post("attendance-take/", {
        teacher_id: selectedTeacher.id,
        status: attendanceStatus,
        month,
        date: selectedDate,
      });
      showToast("Attendance saved!", "success");
      fetchTeacherAttendanceLogs(selectedTeacher.id, month);
    } catch (err) {
      console.error(err);
      showToast("Error saving attendance", "error");
    }
  };

  const fetchTeacherAttendanceLogs = async (teacher_id, monthValue) => {
    if (!teacher_id || !monthValue) return;
    try {
      const token = localStorage.getItem("access");
      const res = await api.get(
        `attendance-take/logs/${teacher_id}/${monthValue}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAttendanceLogs(res.data.logs || []);
      setAttendanceCounts(res.data.counts || {});
      setAttendanceRemark(res.data.remark || "No remark"); // <-- add remark
      setShowDetails(true);
    } catch (err) {
      console.error(err);
      showToast("Error fetching teacher attendance logs", "error");
    }
  };

  const handleEditAttendance = async (status, dateOverride = null) => {
    const dateToUse = dateOverride || selectedDate;
    try {
      await api.post("attendance-take/", {
        teacher_id: selectedTeacher.id,
        status,
        month,
        date: dateToUse,
      });
      showToast("Attendance updated!", "success");
      fetchTeacherAttendanceLogs(selectedTeacher.id, month);
    } catch (err) {
      console.error(err);
      showToast("Error updating attendance", "error");
    }
  };

  const handleBack = () => {
    setSelectedTeacher(null);
    setShowDetails(false);
    setAttendanceStatus("");
    setAttendanceLogs([]);
    setAttendanceCounts({});
    setAttendanceRemark("");
  };

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };
  const handleCloseToast = () => setToast({ ...toast, open: false });

  const chartData = ATTENDANCE_OPTIONS.map((s) => ({
    name: s.replace("_", " ").toUpperCase(),
    count: attendanceCounts[s] || 0,
  }));

  const buildCalendar = () => {
    const monthIndex = MONTHS.indexOf(month);
    const year = new Date().getFullYear();
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);
    const daysInMonth = lastDay.getDate();

    const lookup = {};
    (attendanceLogs || []).forEach((l) => {
      const d = l.date.split("T")[0];
      lookup[d] = l.status;
    });

    const weeks = [];
    let currentWeek = new Array(firstDay.getDay()).fill(null);

    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = new Date(year, monthIndex, d).toISOString().split("T")[0];
      const status = lookup[dateStr] || null;
      currentWeek.push({ day: d, dateStr, status });

      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push(currentWeek);
    }

    return weeks;
  };

  const exportToPDF = () => {
    if (!detailsRef.current) return showToast("Nothing to export", "error");

    const teacherName = selectedTeacher?.full_name || "";
    const headerText = `${teacherName} � Monthly Attendance (${month})`;

    const clone = detailsRef.current.cloneNode(true);

    const header = document.createElement("h2");
    header.style.textAlign = "center";
    header.style.marginBottom = "10px";
    header.style.fontSize = "20px";
    header.style.fontWeight = "bold";
    header.textContent = headerText;
    clone.prepend(header);

    const remarkDiv = document.createElement("div");
    remarkDiv.style.marginTop = "10px";
    remarkDiv.style.fontWeight = "bold";
    remarkDiv.textContent = `Remark: ${attendanceRemark}`;
    clone.appendChild(remarkDiv);

    html2pdf().from(clone).set({
      margin: 0.3,
      filename: `${teacherName.replace(/\s+/g, "_")}_${month}_Attendance.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, logging: false },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
    }).save();
  };

  return (
    <Box p={3} sx={{ backgroundColor: "#f4f4f6", minHeight: "100vh", color: "#333" }}>
      {/* SEARCH */}
      {!selectedTeacher && (
        <Box mb={3} display="flex" justifyContent={isMobile ? "center" : "flex-start"}>
          <TextField
            label="Search Teacher"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 250 }}
          />
        </Box>
      )}

      {/* TEACHER GRID */}
      {!selectedTeacher && (
        <Grid container spacing={3} justifyContent="center" alignItems="center">
          {filteredTeachers.map((teacher) => (
            <Grid key={teacher.id} item xs={6} sm={4} md={2}
              sx={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Avatar alt={teacher.full_name} src={teacherAvatar}
                sx={{ width: 100, height: 100, marginBottom: 1, border: "2px solid #90caf9" }} />
              <Typography variant="subtitle1" fontWeight="bold" sx={{ textAlign: "center", mt: 1 }}>
                {teacher.full_name}
              </Typography>
              <Box mt={1} display="flex" gap={1} flexWrap="wrap" justifyContent="center">
                <Button variant="contained" color="primary" size="small" onClick={() => handleTakeAttendance(teacher)}>
                  Take Attendance
                </Button>
                <Button variant="outlined" color="secondary" size="small" onClick={() => handleTakeAttendance(teacher)}>
                  Details
                </Button>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* DETAILS PANEL */}
      {selectedTeacher && (
        <Paper elevation={3} sx={{ p: 3, mt: 3, backgroundColor: "#fff", color: "#333" }}>
          <Stack spacing={3}>
            <Box display={isMobile ? "block" : "flex"} alignItems="center" gap={2}>
              <IconButton onClick={handleBack}><ArrowBack /></IconButton>

              <Box display="flex" flexDirection={isMobile ? "column" : "row"}
                   alignItems="center" justifyContent={isMobile ? "center" : "flex-start"} mt={isMobile ? 1 : 0}>
                <Avatar alt={selectedTeacher.full_name} src={teacherAvatar}
                  sx={{ width: 120, height: 120, border: "2px solid #90caf9", mb: isMobile ? 1 : 0 }} />
                {!isMobile && (
                  <Typography variant="h5" fontWeight="bold" sx={{ ml: 2 }}>
                    {selectedTeacher.full_name}
                  </Typography>
                )}
              </Box>

              <Box ml="auto" display="flex" gap={1} alignItems="center">
                <FormControl sx={{ minWidth: 160 }}>
                  <InputLabel>Month</InputLabel>
                  <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
                    {MONTHS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </Select>
                </FormControl>

                <Button variant="outlined" onClick={() => fetchTeacherAttendanceLogs(selectedTeacher.id, month)}>
                  Refresh
                </Button>

                <Button variant="contained" startIcon={<PictureAsPdf />} onClick={exportToPDF}>
                  Export PDF
                </Button>
              </Box>
            </Box>

            {isMobile && (
              <Typography variant="h5" fontWeight="bold" textAlign="center">{selectedTeacher.full_name}</Typography>
            )}

            {!showDetails && (
              <>
                <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                  <TextField label="Date" type="date" value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 200 }} />
                </Box>

                <Box display="flex" gap={2} flexWrap="wrap" mt={2}>
                  {ATTENDANCE_OPTIONS.map((option) => (
                    <FormControlLabel key={option}
                      control={<Checkbox checked={attendanceStatus === option} onChange={() => setAttendanceStatus(option)} />}
                      label={option.replace("_", " ").toUpperCase()} />
                  ))}
                </Box>

                <Box display="flex" gap={2} flexWrap="wrap" mt={2}>
                  <Button variant="contained" color="primary" onClick={handleSaveAttendance}>Save</Button>
                  <Button variant="outlined" onClick={() => fetchTeacherAttendanceLogs(selectedTeacher.id, month)}>View Details</Button>
                </Box>
              </>
            )}

            {showDetails && (
              <Box ref={detailsRef}>
                <Typography variant="subtitle1" mb={1}>Monthly Attendance Count ({month})</Typography>
                <Divider sx={{ mb: 1 }} />

                <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center" mb={2}>
                  {ATTENDANCE_OPTIONS.map((s) => (
                    <Paper key={s} sx={{ p: 1.5, minWidth: 120, textAlign: "center", backgroundColor: "#f0f0f3" }}>
                      <Typography variant="body2" fontWeight="bold">{s.replace("_", " ").toUpperCase()}</Typography>
                      <Typography variant="h6">{attendanceCounts[s] || 0}</Typography>
                      <IconButton size="small" sx={{ color: "#1976d2" }} onClick={() => handleEditAttendance(s)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Paper>
                  ))}
                </Box>

                <Box display="grid" gridTemplateColumns={isMobile ? "1fr" : "1fr 380px"} gap={2}>
                  <Box>
                    <Paper sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle2" mb={1}>Calendar - {month}</Typography>
                      <Box>
                        <Box display="grid" gridTemplateColumns="repeat(7,1fr)" gap={0.5} textAlign="center" mb={1}>
                          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
                            <Box key={d} sx={{ fontWeight: "bold", p: 0.5 }}>{d}</Box>
                          ))}
                        </Box>

                        {buildCalendar().map((week, i) => (
                          <Box display="grid" gridTemplateColumns="repeat(7,1fr)" gap={0.5} key={i} mb={0.5}>
                            {week.map((cell, idx) => {
                              if (!cell) return <Box key={idx} sx={{ minHeight: 68, border: "1px solid #eee", p: 1 }} />;
                              const { day, dateStr, status } = cell;
                              return (
                                <Box key={idx} sx={{ minHeight: 68, border: "1px solid #eee", p: 1 }}>
                                  <Typography variant="caption">{day}</Typography>
                                  <Box mt={0.5}>
                                    {status ? (
                                      <Chip label={status.replace("_"," ")} size="small" color={statusColor(status)} />
                                    ) : (
                                      <Typography variant="caption" color="text.secondary">�</Typography>
                                    )}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        ))}
                      </Box>
                    </Paper>

                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" mb={1}>Daily Attendance</Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {attendanceLogs.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={3}>No attendance records for this month</TableCell>
                            </TableRow>
                          )}
                          {attendanceLogs.map((log) => (
                            <TableRow key={log.date}>
                              <TableCell>{new Date(log.date).toLocaleDateString()}</TableCell>
                              <TableCell>
                                <Chip label={log.status.replace("_"," ")} size="small" color={statusColor(log.status)} />
                              </TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  {ATTENDANCE_OPTIONS.map((s) => (
                                    <Button key={s} size="small" variant="outlined" onClick={() => handleEditAttendance(s, log.date)}>
                                      {s[0].toUpperCase()}
                                    </Button>
                                  ))}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Box>

                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" mb={1}>Monthly Bar Chart</Typography>
                    <Box sx={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 10 }}>
                          <XAxis dataKey="name" />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Bar dataKey="count" barSize={36} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary">Counts are based on attendance records for {month}.</Typography>

                    <Paper sx={{ p: 2, mt: 2 }}>
                      <Typography variant="subtitle2" mb={1}>Attendance Remark</Typography>
                      <Typography variant="body1" fontWeight="bold" color={
                        attendanceRemark.includes("Needs Improvement") ? "error" :
                        attendanceRemark.includes("Excellent") ? "success.main" :
                        attendanceRemark.includes("Attention") ? "warning.main" :
                        "text.primary"
                      }>
                        {attendanceRemark}
                      </Typography>
                    </Paper>
                  </Paper>
                </Box>
              </Box>
            )}
          </Stack>
        </Paper>
      )}

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={handleCloseToast}>
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeacherAttendanceData;
